import { memo, type ReactNode, useCallback, useState } from 'react';

import { useAnalyticsUtilities } from '@air/analytics';
import { ZapIcon } from '@air/design-icons';
import { Popover } from '@air/primitive-popover';
import { useAirModal } from '@air/provider-modal';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { UPSELL_FLASH_ICON_BUTTON } from '~/constants/testIDs';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

type UpsellFlashIconButtonProps = {
  children: ReactNode;
};

export const UpsellFlashIconButton = memo<UpsellFlashIconButtonProps>(({ children }) => {
  const [showPlansModal] = useAirModal(PlansModal);
  const [isOpen, setIsOpen] = useState(false);
  const { track } = useAnalyticsUtilities();
  const { data: subscription } = useSubscriptionContext();

  const handleUpgradeClick = useCallback(() => {
    // Track upgrade button clicked with current plan
    track('Upgrade Button Clicked', {
      plan: subscription?.plan || 'unknown',
    });

    showPlansModal();
  }, [track, subscription?.plan, showPlansModal]);

  return (
    <Popover
      side="top"
      align="start"
      data-theme="dark"
      className="max-w-80 border-0"
      open={isOpen}
      onOpenChange={setIsOpen}
      trigger={
        <button
          type="button"
          aria-label="Upgrade to unlock"
          data-testid={UPSELL_FLASH_ICON_BUTTON}
          className="flex items-center justify-center"
          // biome-ignore lint/performance/noJsxPropsBind: the trigger toggles this popover's open state
          onClick={() => setIsOpen(!isOpen)}
        >
          <ZapIcon
            aria-hidden
            className="cursor-pointer ads:text-icon-primary text-blue-9 hover:ads:text-icon-primary hover:text-blue-10"
            size="sm"
          />
        </button>
      }
    >
      <div className="flex items-center gap-3 p-2">
        <ZapIcon aria-hidden className="ads:text-icon-primary text-blue-9" size="xl" />
        <div className="ads:text-primary text-12 text-gray-12">
          {children}{' '}
          <button
            onClick={handleUpgradeClick}
            className="cursor-pointer ads:text-primary text-gray-12 underline ads:hover:text-secondary hover:text-gray-11"
            type="button"
          >
            Click to upgrade.
          </button>
        </div>
      </div>
    </Popover>
  );
});

UpsellFlashIconButton.displayName = 'UpsellFlashIconButton';
